import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ClerkProvider, useAuth, SignIn, SignedIn, SignedOut, RedirectToSignIn, useUser } from '@clerk/clerk-react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { HelmetProvider } from 'react-helmet-async';
import Login from './components/Login';
import { convex } from './lib/convex';
import { Header1 } from './components/ui/Header1';
import { Footerdemo } from './components/ui/footer-section';
import { Features } from './components/Features';
import { TextComparison } from './components/TextComparison';
import BlogWriter from './components/BlogWriter/index';
import Posts from './components/Posts';
import { BlogWriterProvider } from './contexts/BlogWriterContext';
import Contact from './components/Contact';
import ThankYou from './components/ThankYou';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookieSettings from './components/CookieSettings';
import BlogList from './components/blog/BlogList';
import BlogDetail from './components/blog/BlogDetail';
import BlogSection from './components/blog/BlogSection';
import BlogManager from './components/admin/BlogManager';
import BlogEditor from './components/admin/BlogEditor';
import DocsLayout from './components/docs/DocsLayout';
import HomePage from './components/docs/HomePage';
import WriterPage from './components/docs/writer/WriterPage';
import BasicOptionsPage from './components/docs/writer/BasicOptionsPage';
import MarketingOptionsPage from './components/docs/writer/MarketingOptionsPage';
import WritingStylesPage from './components/docs/writer/WritingStylesPage';
import AIModelsPage from './components/docs/writer/AIModelsPage';
import PostsPage from './components/docs/posts/PostsPage';
import ManagingPostsPage from './components/docs/posts/ManagingPostsPage';
import ExportOptionsPage from './components/docs/posts/ExportOptionsPage';
import StrikeListPage from './components/docs/StrikeListPage';
import SettingsPage from './components/docs/SettingsPage';
import PlansPage from './components/docs/PlansPage';
import WhyThisContentTool from './components/WhyThisContentTool';
import Upgrade from './components/Upgrade';
import AuthGuard from './services/AuthGuard';
import { CostCalculator } from './components/CostCalculator';
import { Newsletter } from './components/Newsletter';
import { initGA, pageview } from './lib/analytics';
import CookieConsentBanner from './components/CookieConsentBanner';
import { useClerkDevKeys } from './lib/dev-helpers';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Bell, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SignInButton } from '@clerk/clerk-react';
import { Pricing } from './components/Pricing';
import { motion } from 'framer-motion';
import { ProgressiveLoader } from './components/ProgressiveLoader';
import EmailTemplates from './components/EmailTemplates';
import { SEOHead } from './lib/seo';

function AppContent() {
  const location = useLocation();
  const [showUpdateBanner, setShowUpdateBanner] = useState(true);
  
  // Default SEO for home page
  const defaultSEO = (
    <SEOHead
      title="AI Content Writer That Doesn't Suck - AISEOBlogGenerator"
      description="Stop wrestling with AI prompts that produce robotic content. Create SEO-optimized, human-like blog posts that actually convert with our AI-powered content writer built specifically for marketers and small businesses."
      type="website"
      canonicalUrl="https://aiseobloggenerator.com/"
    />
  );
  
  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);
  
  // Track page views
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  
  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Hero section component with two-column layout
  const HeroSection = () => (
    <div className="pt-24 bg-cream relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-dogbrown/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-dogblue/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4">
        {/* Update notification banner */}
        {showUpdateBanner && (
          <div className="mb-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-lg shadow-md relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bell className="w-5 h-5 mr-2 animate-pulse" />
                <p className="font-medium">
                  <span className="font-bold">New:</span> Claude 4 Sonnet & Opus models now available! Experience our most advanced AI capabilities.
                </p>
              </div>
              <button 
                onClick={() => setShowUpdateBanner(false)}
                className="text-white/80 hover:text-white"
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left column - Text content */}
          <div className="text-left">
            <div className="mb-6 inline-block">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black text-white">
                <Star className="w-3.5 h-3.5 mr-1.5" />
                AI CONTENT THAT DOESN'T SUCK FINALLY
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="relative inline-block">
                Finally,
                <span className="absolute -top-6 right-0 text-3xl transform rotate-6">🐕</span>
              </span>{' '}
              AI content that doesn't make you want to delete your site.
            </h1>
            <p className="text-lg md:text-xl mb-8 text-gray-700">
              AiSEOBlogGenerator.com isn't just fast. It's actually usable.
              SEO-smart, personality-packed, and weirdly good.
            </p>
            <div className="flex flex-wrap gap-4">
              <SignInButton mode="modal">
                <Button size="lg" className="bg-black hover:bg-black/80 text-white px-6 py-3 text-base font-bold">
                  Try It For Free
                </Button>
              </SignInButton>
              
              <Link to="/why">
                <Button size="lg" variant="outline" className="px-6 py-3 text-base">
                  Learn Why It's Different
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Right column - Image with animations */}
          <div className="flex justify-center">
            <motion.div
              initial={{ scale: 1, rotate: 0 }}
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [-2, 2, -2], 
                y: [-5, 5, -5]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut" 
              }}
              className="relative"
            >
              <img 
                src="https://emp3.b-cdn.net/seoblogtool/png/noneofthis.png"
                alt="None of this content"
                className="w-full max-h-[300px] object-contain mx-auto"
              />
            </motion.div>
          </div>
        </div>

        {/* Additional Hero Elements */}
        <div className="mt-24">
          {/* Feature showcase with dog emojis - Enhanced design */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 mb-16">
            {/* Card 1: Choose Your Content Framework */}
            <motion.div 
              className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="absolute -right-5 -top-5 w-24 h-24 bg-dogbrown/5 rounded-full"></div>
              <motion.div 
                className="absolute right-4 top-4 text-5xl"
                initial={{ rotate: 12 }}
                whileHover={{ rotate: 0, scale: 1.2 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                🐕
              </motion.div>
              <div className="relative z-10">
                <div className="w-12 h-1 bg-dogbrown mb-4 rounded-full"></div>
                <h3 className="text-xl font-bold mb-3 pr-14 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">Choose Your Content Framework</h3>
                <p className="text-sm text-gray-700">Pick a structure, not just a title. Get content that works, not just words on a page.</p>
              </div>
            </motion.div>
            
            {/* Card 2: CTAs That Convert */}
            <motion.div 
              className="bg-gradient-to-br from-white to-green-50 p-6 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="absolute -right-5 -top-5 w-24 h-24 bg-green-600/5 rounded-full"></div>
              <motion.div 
                className="absolute right-4 top-4 text-5xl"
                initial={{ rotate: 12 }}
                whileHover={{ rotate: 0, scale: 1.2 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                🐶
              </motion.div>
              <div className="relative z-10">
                <div className="w-12 h-1 bg-green-500 mb-4 rounded-full"></div>
                <h3 className="text-xl font-bold mb-3 pr-14 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">CTAs That Convert</h3>
                <p className="text-sm text-gray-700">Choose intent (Buy, Book, Learn, Laugh) and get CTAs that don't reek of desperation.</p>
              </div>
            </motion.div>
            
            {/* Card 3: Longform That Doesn't Feel Long */}
            <motion.div 
              className="bg-gradient-to-br from-white to-amber-50 p-6 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
              whileHover={{ 
                y: -5, 
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="absolute -right-5 -top-5 w-24 h-24 bg-amber-600/5 rounded-full"></div>
              <motion.div 
                className="absolute right-4 top-4 text-5xl"
                initial={{ rotate: 12 }}
                whileHover={{ rotate: 0, scale: 1.2 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                🦮
              </motion.div>
              <div className="relative z-10">
                <div className="w-12 h-1 bg-amber-500 mb-4 rounded-full"></div>
                <h3 className="text-xl font-bold mb-3 pr-14 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">Longform That Doesn't Feel Long</h3>
                <p className="text-sm text-gray-700">Content that keeps readers engaged from start to finish, not a word dump.</p>
              </div>
            </motion.div>
            
            {/* Card 4: Not Dog S*t */}
            <motion.div 
              className="bg-gradient-to-br from-white to-rose-50 p-6 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="absolute -right-5 -top-5 w-24 h-24 bg-rose-600/5 rounded-full"></div>
              <motion.div 
                className="absolute right-4 top-4 text-5xl"
                initial={{ rotate: 12 }}
                whileHover={{ rotate: 0, scale: 1.2 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                🐕‍🦺
              </motion.div>
              <div className="relative z-10">
                <div className="w-12 h-1 bg-rose-500 mb-4 rounded-full"></div>
                <h3 className="text-xl font-bold mb-3 pr-14 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">Not Dog S*t</h3>
                <p className="text-sm text-gray-700">Every output's clean, interesting, and cringe-free. Finally.</p>
              </div>
            </motion.div>
          </div>
          
          {/* Social proof */}
          <div className="mb-16 text-center">
            <div className="flex items-center justify-center mb-4 space-x-2">
              <div className="flex -space-x-2">
                <div className="w-10 h-10 rounded-full bg-dogbrown text-white flex items-center justify-center text-sm font-bold">GF</div>
                <div className="w-10 h-10 rounded-full bg-dogblue text-white flex items-center justify-center text-sm font-bold">JD</div>
                <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center text-sm font-bold">SB</div>
              </div>
              <span className="text-sm text-gray-500 ml-2">+420 other smart marketers</span>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="ml-2 text-gray-700 font-medium">4.9/5 stars from people who are sick of AI garbage</span>
            </div>
          </div>
          
          {/* Testimonial */}
          <div className="max-w-3xl mx-auto mb-20 bg-white p-8 rounded-lg shadow-md relative">
            <p className="italic text-gray-700 text-lg">
              "I was tired of AI-generated content that sounds like it was written by a robot having an existential crisis. This tool actually produces stuff I can publish without extensive editing."
            </p>
            <div className="mt-4 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-dogbrown flex items-center justify-center text-white font-bold mr-3">
                M
              </div>
              <div className="text-left">
                <p className="font-semibold">Marketer Who Values Their Time</p>
                <p className="text-sm text-gray-500">Like those 2 guys living in a van down by the river</p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center mb-12">
            <Link to="/writer">
              <Button size="lg" className="bg-black hover:bg-black/80 text-white px-8 py-6 text-lg">
                Write Better Content Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <BlogWriterProvider>
      <HelmetProvider>
        <div className="min-h-screen flex flex-col bg-cream">
          <Header1 />
          <AuthGuard>
            <Routes>
              <Route 
                path="/" 
                element={
                <>
                  <HeroSection />
                  {defaultSEO}
                  <div className="space-y-0">
                    <CostCalculator />
                    <TextComparison />
                    <Features />
                    <Newsletter />
                    <BlogSection />
                    <Pricing />
                    <Contact />
                  </div>
                  <Footerdemo />
                </>
                }
              />
              
              <Route 
                path="/why" 
                element={<WhyThisContentTool />}
              />

              <Route
                path="/posts"
                element={
                  <main className="flex-1">
                    <Posts />
                  </main>
                }
              />
              <Route
                path="/posts/:slug"
                element={
                  <main className="flex-1">
                    <Posts />
                  </main>
                }
              />
              <Route
                path="/writer"
                element={
                  <main className="flex-1">
                    <BlogWriter />
                  </main>
                }
              />
              <Route
                path="/login"
                element={<Login />}
              />
              <Route
                path="/upgrade"
                element={<Upgrade />}
              />
              <Route
                path="/thank-you"
                element={<ThankYou />}
              />
              <Route
                path="/contact"
                element={
                  <>
                    <main className="flex-1 pt-16">
                      <Contact />
                    </main>
                    <Footerdemo />
                  </>
                }
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookie-settings" element={<CookieSettings />} />
              
              {/* Email Templates Preview Route */}
              <Route path="/emailtemplates" element={<EmailTemplates />} />

              {/* Blog Routes */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/tag/:tag" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />

              {/* Admin Routes */}
              <Route
                path="/admin/blog"
                element={<BlogManager />}
              />
              <Route
                path="/admin/blog/new"
                element={<BlogEditor />}
              />
              <Route
                path="/admin/blog/edit/:postId"
                element={<BlogEditor />}
              />

              {/* Documentation Routes */}
              <Route path="/docs" element={<DocsLayout />}>
                <Route index element={<HomePage />} />
                <Route path="writer" element={<WriterPage />} />
                <Route path="writer/basic-options" element={<BasicOptionsPage />} />
                <Route path="writer/marketing-options" element={<MarketingOptionsPage />} />
                <Route path="writer/writing-styles" element={<WritingStylesPage />} />
                <Route path="writer/ai-models" element={<AIModelsPage />} />
                <Route path="posts" element={<PostsPage />} />
                <Route path="posts/managing" element={<ManagingPostsPage />} />
                <Route path="posts/export" element={<ExportOptionsPage />} />
                <Route path="strike-list" element={<StrikeListPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="plans" element={<PlansPage />} />
              </Route>
            </Routes>
          </AuthGuard>
          <CookieConsentBanner />
        </div>
      </HelmetProvider>
    </BlogWriterProvider>
  );
}

export default function App() {
  // Add loading state to show something while Clerk initializes
  const [isClerkLoading, setIsClerkLoading] = React.useState(true);
  const { publishableKey, isLoading: isKeysLoading } = useClerkDevKeys();

  useEffect(() => {
    // Simulate a loading time for Clerk to initialize
    const timer = setTimeout(() => {
      setIsClerkLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  if (isClerkLoading || isKeysLoading) {
    return <ProgressiveLoader />;
  }

  // Get the Clerk publishable key from our helper or fallback
  const clerkKey = publishableKey || import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "";

  // Check if we have a valid key
  if (!clerkKey) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold mb-2">Missing Clerk API Key</h1>
          <p className="text-lg mb-4">
            The application couldn't find a valid Clerk API key. Please add a VITE_CLERK_PUBLISHABLE_KEY to your .env file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider 
      publishableKey={clerkKey}
      waitlistUrl="https://clerk.aiseobloggenerator.com"
      afterSignInUrl="/upgrade"
      afterSignUpUrl="/upgrade"
      signUpUrl="/sign-up"
    >
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <AppContent />
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}